/**
 * Empty-Scope Hint Builder.
 *
 * When a scope's analyzers all return no observations, the user previously
 * saw a blank card with no explanation. This helper emits a single info-
 * severity observation that says WHY it's empty: usually "sample too small,
 * keep trading and Coach will start surfacing patterns at N trades."
 *
 * Pure: takes scope info + filtered trade count, returns one observation
 * (or null when there's literally no data — UI shows the no-trades empty
 * state in that case).
 *
 * Used by: scopes/setup.js, scopes/atm.js, scopes/account.js when their
 * analyzer loops produce nothing but trade-count is non-zero.
 */

import { CATEGORY, SEVERITY, makeObservation } from './types.js';

// Minimum trades each scope needs to start producing meaningful signal.
// Matches the floors used inside the composite analyzers; pulled here so
// the hint message stays in sync if those floors change.
const SCOPE_FLOORS = {
  setup:   6,
  atm:     6,
  account: 8,
  day:     1,    // day scope always has SOMETHING to say if there are trades
};

const SCOPE_LABEL = {
  setup:   (id) => `setup "${id}"`,
  atm:     (id) => `ATM "${id}"`,
  account: (id) => `account "${id}"`,
  day:     ()   => 'today',
};

/**
 * Build a "needs more data" hint observation when a scope's analyzers all
 * return empty. Returns null when:
 *   - tradeCount is 0 (UI handles the empty state separately — no point
 *     telling them "Coach needs more data" when the answer is "you need
 *     to trade first")
 *   - tradeCount is already AT or ABOVE the floor (analyzers returning
 *     null at full sample is a different problem; surfacing a "needs more
 *     data" hint here would be misleading)
 *
 * @param {Object} args
 * @param {string} args.scope     'setup' | 'atm' | 'account' | 'day'
 * @param {string} args.scopeId   The entity name/id
 * @param {number} args.tradeCount  How many trades the scope was actually
 *                                  filtered to (post-enrichment)
 * @returns {Observation|null}
 */
export const buildEmptyScopeHint = ({ scope, scopeId = '', tradeCount = 0 } = {}) => {
  if (!scope) return null;
  if (tradeCount <= 0) return null;
  const floor = SCOPE_FLOORS[scope];
  if (floor && tradeCount >= floor) return null;

  const labelFn = SCOPE_LABEL[scope] || ((id) => id || scope);
  const noun = labelFn(scopeId);
  const need = floor || 4;
  const remaining = Math.max(1, need - tradeCount);

  return makeObservation({
    id: `emptyScopeHint:${scope}:${scopeId || 'x'}`,
    analyzer: 'emptyScopeHint',
    scope,
    scopeId,
    severity: SEVERITY.INFO,
    category: CATEGORY.PROCESS,
    title: `Coach needs more trades on ${noun} (${tradeCount}/${need})`,
    detail: `Most analyzers need at least ${need} trades on a single ${scope} before signal separates from noise. ${remaining} more trade${remaining === 1 ? '' : 's'} on this ${scope} and Coach will start surfacing patterns automatically.`,
    action: scope === 'setup' || scope === 'atm'
      ? `Keep trading this ${scope} as you normally would. Coach will fill this card in once the sample is real.`
      : `Keep building history on this ${scope}.`,
    evidence: { tradeCount, requiredFloor: need, remaining },
    confidence: 1.0,
    priority: 30,  // low — info-level placeholder, never outranks real signal
  });
};

// Exports for tests
export { SCOPE_FLOORS, SCOPE_LABEL };
